import { documentTypeSchema, uploadFileSchema, type UploadFile } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { decode } from 'base64-arraybuffer'
import { getDocumentAsync } from 'expo-document-picker'
import { EncodingType, readAsStringAsync } from 'expo-file-system/legacy'
import { useCallback, useState } from 'react'
import { Platform, View } from 'react-native'
import z from 'zod/v4'
import { PhaseWrapper } from '~/components/flows/phases/PhaseWrapper.tsx'
import { useAlert } from '~/components/GlobalAlert.tsx'
import Icon from '~/components/Icon'
import { ViewTitle } from '~/components/layout/Sections.tsx'
import SourceViewer from '~/components/source-viewer'
import { Body } from '~/components/typography/Content.tsx'
import { Button } from '~/components/ui/button'

export const documentInputSchema = z.object({
  file: uploadFileSchema,
})

export type DocumentInputData = z.infer<typeof documentInputSchema>

export function DocumentInputPhase({
  documentData,
  onUpdateData,
  disabled,
}: {
  documentData?: DocumentInputData
  onUpdateData: (data: DocumentInputData) => unknown
  disabled?: boolean
}) {
  const { showAlert } = useAlert()
  const { t } = useTranslations('property')

  const [selectedFile, setSelectedFile] = useState<UploadFile | null>(documentData?.file || null)
  const [isPdfValidated, setIsPdfValidated] = useState(!!documentData?.file)

  const handlePickAndExtract = useCallback(async () => {
    if (disabled) {
      return
    }

    try {
      const result = await getDocumentAsync({
        type: ['application/pdf'],
        copyToCacheDirectory: true,
        multiple: false,
      })

      if (result.canceled) {
        return
      }

      const asset = result.assets[0]
      const fileType = documentTypeSchema.safeParse(asset.mimeType).data

      if (!fileType) {
        throw new Error(t(($) => $.flow.document.error.invalidPdf))
      }

      let pdfData: ArrayBuffer

      if (Platform.OS === 'web') {
        const response = await fetch(asset.uri)
        pdfData = await response.arrayBuffer()
      } else {
        const response = await readAsStringAsync(asset.uri, {
          encoding: EncodingType.Base64,
        })

        pdfData = decode(response)
      }

      setSelectedFile({
        arrayBuffer: pdfData,
        path: asset.uri,
        name: asset.name || '',
        type: fileType,
        size: asset.size,
      })

      setIsPdfValidated(false)
    } catch (error: unknown) {
      showAlert({
        type: 'error',
        title: t(($) => $.flow.document.error.title),
        description:
          error instanceof Error
            ? error.message
            : t(($) => $.flow.document.error.fileSelectionFailed),
      })
    }
  }, [disabled, showAlert, t])

  const handlePdfLoad = useCallback(() => {
    setIsPdfValidated(true)

    if (selectedFile) {
      onUpdateData({ file: selectedFile })
    }
  }, [selectedFile, onUpdateData])

  const handlePdfError = useCallback(() => {
    setIsPdfValidated(false)
  }, [])

  if (selectedFile) {
    return (
      <PhaseWrapper>
        <ViewTitle>
          {isPdfValidated ? t(($) => $.flow.document.loaded) : t(($) => $.flow.document.loading)}
        </ViewTitle>

        <View className="flex-1 gap-4">
          <SourceViewer
            source={selectedFile.path}
            type="pdf"
            onLoad={handlePdfLoad}
            onError={handlePdfError}
            className="native:shadow-card h-[33vh] min-h-[33vh] flex-1 items-center justify-center overflow-hidden rounded-lg border-2 border-muted bg-muted text-secondary web:shadow-md web:shadow-secondary/50"
          />

          {!isPdfValidated && (
            <Body className="text-neutral-500">{t(($) => $.flow.document.validating)}</Body>
          )}

          {isPdfValidated && (
            <View className="flex-row items-center gap-2">
              <Icon name="check" className="text-secondary" size={16} />
              <Body className="text-secondary">{t(($) => $.flow.document.valid)}</Body>
            </View>
          )}

          <Button
            onPress={handlePickAndExtract}
            className="mt-4"
            variant="outline"
            disabled={false}>
            {t(($) => $.flow.document.selectAnotherFile)}
          </Button>
        </View>
      </PhaseWrapper>
    )
  }

  return (
    <PhaseWrapper>
      <ViewTitle>{t(($) => $.flow.document.title)}</ViewTitle>
      <View className="items-center">
        <Button onPress={handlePickAndExtract} variant="default" disabled={false}>
          {t(($) => $.flow.document.selectFile)}
        </Button>
      </View>
      <Body className="text-center text-foreground">{t(($) => $.flow.document.description)}</Body>
    </PhaseWrapper>
  )
}
